import type React from "react";
import { useCallback } from "react";
import type { PanelEntityType } from "@/v2/stores/panelStore";
import { usePanelStore } from "@/v2/stores/panelStore";
import { useIsInsidePanel } from "./PanelContext";

interface PanelLinkProps {
  entityType: PanelEntityType;
  entityId: string | number;
  label?: string;
  fallbackHref: string;
  className?: string;
  children: React.ReactNode;
  /** Called after the panel opens — e.g. to close a surrounding sheet. */
  onNavigate?: () => void;
}

export function PanelLink({
  entityType,
  entityId,
  label,
  fallbackHref,
  className,
  children,
  onNavigate,
}: PanelLinkProps) {
  const isInsidePanel = useIsInsidePanel();
  const { openPanel, pushPanel } = usePanelStore();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Allow ctrl/cmd-click to open in new tab
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();

      const entry = { entityType, entityId: String(entityId), label };

      if (isInsidePanel) {
        pushPanel(entry);
      } else {
        openPanel(entry);
      }
      onNavigate?.();
    },
    [
      entityType,
      entityId,
      label,
      isInsidePanel,
      openPanel,
      pushPanel,
      onNavigate,
    ]
  );

  return (
    <a
      href={fallbackHref}
      onClick={handleClick}
      className={className ?? "tw-text-sm tw-text-primary hover:tw-underline"}
    >
      {children}
    </a>
  );
}

export function usePanelNavigation() {
  const isInsidePanel = useIsInsidePanel();
  const { openPanel, pushPanel } = usePanelStore();

  return useCallback(
    (
      entityType: PanelEntityType,
      entityId: string | number,
      label?: string
    ) => {
      const entry = { entityType, entityId: String(entityId), label };
      if (isInsidePanel) {
        pushPanel(entry);
      } else {
        openPanel(entry);
      }
    },
    [isInsidePanel, openPanel, pushPanel]
  );
}
